import React, { Component, RefObject } from 'react';
import { TooltipPlacement } from 'antd/lib/tooltip';
import cx from 'classnames';
import { SxTooltip } from '../utils/wrapComponents';
import styles from './index.module.scss';

const CONTAINER_HEIGHT_DELTA = 2;

interface AutoEllipsisTooltipProps {
  text: React.ReactNode | string;
  maxTextLines?: number;
  className?: string;
  tooltipClassName?: string;
  tooltipText?: string;
  placement?: TooltipPlacement;
  fullWidth?: boolean;
  textAlign?: 'left' | 'right';
}

interface AutoEllipsisTooltipState {
  showTooltip: boolean;
}

export class AutoEllipsisTooltip extends Component<AutoEllipsisTooltipProps, AutoEllipsisTooltipState> {
  container: RefObject<HTMLSpanElement> = React.createRef();

  constructor(props: AutoEllipsisTooltipProps) {
    super(props);
    this.state = {
      showTooltip: false
    };
    this.updateDimensions = this.updateDimensions.bind(this);
  }

  componentDidMount(): void {
    window.addEventListener('resize', this.updateDimensions);
    this.testTooltip();
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.updateDimensions);
  }

  private updateDimensions(): void {
    this.testTooltip();
  }

  private testTooltip(): void {
    const { showTooltip } = this.state;

    if (this.container && this.container.current) {
      const { maxTextLines } = this.props;
      const isOverflowing = maxTextLines && maxTextLines > 1
        ? this.container.current.offsetHeight < (this.container.current.scrollHeight - CONTAINER_HEIGHT_DELTA)
        : this.container.current.offsetWidth < this.container.current.scrollWidth;

      if (isOverflowing && !showTooltip) {
        this.setState({
          showTooltip: true
        });
      } else if (!isOverflowing && showTooltip) {
        this.setState({
          showTooltip: false
        });
      }
    }
  }

  private renderContent(): JSX.Element {
    const { text, maxTextLines = 1, className: outerClassName, fullWidth, textAlign } = this.props;
    const className = cx(
      styles.container,
      { [styles.fullWidth]: fullWidth },
      { [styles[textAlign || '']]: textAlign },
      { [styles.singleLine]: maxTextLines && maxTextLines <= 1 },
      { [styles.multiLine]: maxTextLines && maxTextLines > 1 },
      outerClassName
    );
    const style = maxTextLines && maxTextLines > 1 ? { WebkitLineClamp: maxTextLines } : {};

    return (
      <span ref={this.container} className={className} style={style}>
        {text}
      </span>
    );
  }

  render(): JSX.Element {
    const { showTooltip } = this.state;
    const { text, placement = 'bottom', tooltipText } = this.props;
    if (!showTooltip) {
      return this.renderContent();
    }
    return (
      <SxTooltip
        trigger={['hover']}
        Component={this.renderContent()}
        tooltipText={tooltipText || (typeof text === 'string' ? text : '')}
        placement={placement}
      />
    );
  }
}
